import React from 'react';
import { FiArrowDown, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { useExecutiveFunnel } from '../../hooks/useExecutiveQueries';
import { enhancedColors } from '../../styles/theme';
import { LoadingSpinner, KPICard } from '../ui';

interface CustomerClubPerformanceFunnelProps {
  branchId?: number;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

interface FunnelStage {
  stage: string;
  value: number;
  percentage: number;
  unit: string;
  stageOrder: number;
}
const TrendingUpIcon = FiTrendingUp as React.ElementType;
const TrendingDownIcon = FiTrendingDown as React.ElementType;
const ArrowDownIcon = FiArrowDown as React.ElementType;

const CustomerClubPerformanceFunnel: React.FC<CustomerClubPerformanceFunnelProps> = ({
  branchId,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  // Fetch funnel data with timeRange
  const { data: funnelData = [], isLoading, error } = useExecutiveFunnel(branchId, timeRange?.timeRange);
  
  // Enhanced funnel data with colors
  const enhancedFunnelData = funnelData.map((stage: FunnelStage, index: number) => ({
    ...stage,
    icon: ['💰', '📄', '🎫', '👥', '✅', '🌟'][index] || '📊',
    color: enhancedColors.primary[index % enhancedColors.primary.length],
    description: `مرحله ${stage.stageOrder} از فرآیند تبدیل`
  }));
  
  // Get variant based on performance
  const getVariant = (percentage: number) => {
    if (percentage < 20) return 'error';
    if (percentage > 80) return 'success';
    if (percentage > 50) return 'info';
    return 'warning';
  };

  // Get icon based on percentage trend
  const getTrendIcon = (percentage: number) => {
    if (percentage > 80) return <TrendingUpIcon size={20} />;
    if (percentage < 20) return <TrendingDownIcon size={20} />;
    return null;
  };
  
  // Handle loading state
  if (showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[300px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری قیف عملکرد…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (error) {
    if (onError && error instanceof Error) {
      onError(error);
    }
    return null;
  }
  
  return (
    <div className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <h3 className="title-card">قیف عملکرد باشگاه مشتریان</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">نمایش مراحل مصرف اعتبار در بازه زمانی انتخاب شده</p>
      </div>
      
      <div className="space-y-4">
        {enhancedFunnelData.length > 0 ? (
          enhancedFunnelData.map((stage: any, index: number) => (
            <div key={index} className="relative">
              <KPICard
                title={stage.stage}
                value={stage.value.toLocaleString()}
                subtitle={`${stage.percentage}% - ${stage.description}`}
                currency={stage.unit}
                variant={getVariant(stage.percentage) as any}
                accentStyle="corner"
                icon={getTrendIcon(stage.percentage)}
                className="shadow-sm hover:shadow-md transition-all"
              />
              
              {/* Arrow connector */}
              {index < enhancedFunnelData.length - 1 && (
                <div className="flex-center my-2">
                  <div className="relative">
                    <ArrowDownIcon size={24} className="content-tertiary animate-bounce" aria-hidden="true" />
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-text-tertiary">
            <p>داده‌ای برای نمایش قیف وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerClubPerformanceFunnel;
